import { TranslateService } from '@ngx-translate/core';
import { filter, lastValueFrom, ReplaySubject, take } from 'rxjs';
import { BasketService } from 'src/app/checkout-page/services/basket.service';
import { NotificationModel } from 'src/app/notification/models/notification';
import { MapToGA4 } from 'src/business/Mapper/map-to-ga4-item';
import { Address, BasketReturnModel, CustomerType, InvoiceDetailRequestModel, Origin, PaymentMethod, PaymentReturnModel, PickupPoint, ShipmentReturnModel, ShippingMethodReturnModel, SwaggerException, UpdateBasketAddressModel } from 'src/business/services/client.generated';
import { GA4 } from 'src/externals/tag-manager/models/ga4.model';
import { StepperComponent } from 'src/layout/stepper/stepper.component';
import { DomUtils } from 'src/utils/dom.util';
import { countriesMock } from '../countries.mock';

export namespace StandardForms {
    export type InvoiceOption = {}
    export type FormModel = {
        legend: string,
        inputs: InputModel[]
    }
    export type InputModel = {
        label?: string;
        placeholder?: string;
        translatePrefix?: string;
        name: string;
        value?: string;
        tag?: string;
        options?: Array<{ id: string, label?: string, translateKey?: string, data?: { [key: string]: any } }>;
        datalist?: Array<string>;
        attributes?: { [key: string]: string | boolean | number | RegExp }

        type?: 'button' |
        'checkbox' |
        'color' |
        'date' |
        'datetime-local' |
        'email' |
        'hidden' |
        'image' |
        'month' |
        'number' |
        'password' |
        'radio' |
        'range' |
        'reset' |
        'search' |
        'submit' |
        'tel' |
        'text' |
        'time' |
        'url' |
        'week';
        autocomplete?: 'on' |
        'off' |
        'name' |
        'honorific-prefix' |
        'given-name' |
        'additional-name' |
        'family-name' |
        'honorific-suffix' |
        'nickname' |
        'email' |
        'username' |
        'new-password' |
        'current-password' |
        'one-time-code' |
        'organization-title' |
        'organization' |
        'street-address' |
        'address-line1' |
        'address-line2' |
        'address-line3' |
        'address-level4' |
        'address-level3' |
        'address-level2' |
        'address-level1' |
        'country' |
        'country-name' |
        'postal-code' |

        'shipping street-address' |
        'shipping address-line1' |
        'shipping address-line2' |
        'shipping address-line3' |
        'shipping address-level4' |
        'shipping address-level3' |
        'shipping address-level2' |
        'shipping address-level1' |
        'shipping country' |
        'shipping country-name' |
        'shipping postal-code' |

        'billing street-address' |
        'billing address-line1' |
        'billing address-line2' |
        'billing address-line3' |
        'billing address-level4' |
        'billing address-level3' |
        'billing address-level2' |
        'billing address-level1' |
        'billing country' |
        'billing country-name' |
        'billing postal-code' |

        'cc-name' |
        'cc-given-name' |
        'cc-additional-name' |
        'cc-family-name' |
        'cc-number' |
        'cc-exp' |
        'cc-exp-month' |
        'cc-exp-year' |
        'cc-csc' |
        'cc-type' |

        'transaction-currency' |
        'transaction-amount' |

        'language' |

        'bday' |
        'bday-day' |
        'bday-month' |
        'bday-year' |

        'tel' |
        'tel-country-code' |
        'tel-national' |
        'tel-area-code' |
        'tel-local' |
        'tel-extension' |

        'impp' |
        'url' |
        'webauthn';
        data?: { [key: string]: any }
    }

    export namespace Input {
        const phoneRegex = /(\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1))*\s*[\d\s]*/;
        export const reference = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.REFERENCE`,
            translatePrefix: `ADDRESS.REFERENCE`,
            name: 'reference',
            autocomplete: 'off',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const firstName = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.FIRST_NAME`,
            translatePrefix: `ADDRESS.FIRST_NAME`,
            name: 'firstName',
            autocomplete: section + ' given-name',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const lastName = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.LAST_NAME`,
            translatePrefix: `ADDRESS.LAST_NAME`,
            name: 'lastName',
            autocomplete: section + ' family-name',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const email = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.EMAIL`,
            translatePrefix: `ADDRESS.EMAIL`,
            name: 'email',
            autocomplete: section + ' email',
            type: 'email',
            tag: 'input',

        } as InputModel)
        export const organizationName = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.ORGANIZATION_NAME`,
            translatePrefix: `ADDRESS.ORGANIZATION_NAME`,
            name: 'organizationName',
            autocomplete: section + ' organization',
            type: 'text',
            tag: 'input',
        } as InputModel)
        export const line1 = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.LINE1`,
            translatePrefix: `ADDRESS.LINE1`,
            name: 'line1',
            autocomplete: section + ' address-line1',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const line2 = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.LINE2`,
            translatePrefix: `ADDRESS.LINE2`,
            name: 'line2',
            autocomplete: section + ' ',
            type: 'text',
            tag: 'input',
        } as InputModel)
        export const city = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.CITY`,
            translatePrefix: `ADDRESS.CITY`,
            name: 'city',
            autocomplete: section + ' address-level2',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const postalCode = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.POSTAL_CODE`,
            translatePrefix: `ADDRESS.POSTAL_CODE`,
            name: 'postalCode',
            autocomplete: section + ' postal_code',
            type: 'text',
            tag: 'input',

        } as InputModel)
        export const regionCode = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.REGION_CODE`,
            translatePrefix: `ADDRESS.REGION_CODE`,
            name: 'regionCode',
            autocomplete: section + ' ',
            type: 'text',
            tag: 'input',
        } as InputModel)
        export const country = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.COUNTRY`,
            translatePrefix: `ADDRESS.COUNTRY`,
            name: 'country',
            autocomplete: section + ' country',
            tag: 'select',
            attributes: {
                required: '', novalidate: '',
            },
            options: countriesMock.filter(c => c.code.toLowerCase() == document.body.parentElement.lang.split('-')[document.body.parentElement.lang.split('-').length - 1].toLowerCase()).map(country => ({ id: country.code, label: country.name }))
        } as InputModel)
        export const phoneNumber = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.PHONE_NUMBER`,
            translatePrefix: `ADDRESS.PHONE_NUMBER`,
            name: 'phoneNumber',
            autocomplete: section + ' tel',
            type: 'text',
            tag: 'input',
            attributes: {
                pattern: phoneRegex.source
            }
        } as InputModel)
        export const mobilePhoneNumber = (section: string, value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.${section}.MOBILE_PHONE_NUMBER`,
            translatePrefix: `ADDRESS.MOBILE_PHONE_NUMBER`,
            name: 'mobilePhoneNumber',
            autocomplete: section + ' tel',
            type: 'text',
            tag: 'input',
            attributes: {
                pattern: phoneRegex.source
            }

        } as InputModel)
        export const Note = (value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.NOTE`,
            name: 'note',
            autocomplete: 'off',
            tag: 'textarea',
            attributes: {
                rows: "4",
            }
        } as InputModel)

        export const GLN = (value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.GLN`,
            name: 'gln',
            autocomplete: 'on',
            type: 'text',
            tag: 'input',
        } as InputModel)

        export const CVR = (value?: string) => ({
            value,
            placeholder: `ADDRESS.PLACEHOLDER.CVR`,
            name: 'cvr',
            autocomplete: 'on',
            type: 'text',
            tag: 'input',
        } as InputModel)
        export const Pickup = (pickup: PickupPoint, shipment?: ShipmentReturnModel) => ({
            value: pickup.id,
            label: (pickup.address.firstName + ' ' + pickup.address.lastName + ' ' + pickup.address.organizationName).trimStart().trimEnd(),
            name: 'pickupPointId',
            type: 'radio',
            tag: 'input',
            attributes: { required: '', checked: shipment?.pickupPointId == pickup.id ? '' : null }
        })
        export const ShippingMethod = (method: ShippingMethodReturnModel, shipment?: ShipmentReturnModel) => ({
            value: method.id,
            label: method.name,
            name: 'shippingMethodId',
            type: 'radio',
            autocomplete: null,
            tag: 'input',
            attributes: { required: '', checked: shipment != null ? '' : null },
            data: { method: { ...method, price: { ...method.price, salesPrice: { amount: method.price.salesPrice.amount - method.price.vatAmount.amount, currency: method.price.salesPrice.currency } } } }
        } as InputModel)
        export const ShippingMethodWithPickups = (method: ShippingMethodReturnModel, shipment?: ShipmentReturnModel, pickups?: PickupPoint[]) => ({
            value: method.id,
            label: method.name,
            name: 'shippingMethodId',
            type: 'radio',
            autocomplete: null,
            tag: 'input',
            attributes: { required: '', checked: shipment != null ? '' : null },
            data: { method, pickups: pickups.map(pickup => Pickup(pickup, shipment)) }
        } as InputModel)
        const PaymentMethodIcons = (id: string) => {
            let translations = JSON.parse(sessionStorage.getItem('translations') ?? '{"PAYMENT_CARD":{}}');

            if (id == 'dankort')
                return [{ imageUrl: '/dist/assets/cards/DANKORT.svg', alt: translations['PAYMENT_CARD.DANKORT'] }];
            if (id == 'mastercard')
                return [{ imageUrl: '/dist/assets/cards/MASTERCARD.svg', alt: translations['PAYMENT_CARD.MASTERCARD'] }];
            if (id == 'visa')
                return [{ imageUrl: '/dist/assets/cards/VISA_ELECTRON.svg', alt: translations['PAYMENT_CARD.VISA_ELECTRON'] }];
            if (id == 'visa_mastercard')
                return [{ imageUrl: '/dist/assets/cards/VISA.svg', alt: translations['PAYMENT_CARD.VISA'] }, { imageUrl: '/dist/assets/cards/MASTERCARD.svg', alt: translations['PAYMENT_CARD.MASTERCARD'] }];
            if (id == 'creditcard')
                return [{ imageUrl: '/dist/assets/cards/DANKORT.svg', alt: translations['PAYMENT_CARD.DANKORT'] }, { imageUrl: '/dist/assets/cards/VISA.svg', alt: translations['PAYMENT_CARD.VISA'] }, { imageUrl: '/dist/assets/cards/MASTERCARD.svg', alt: translations['PAYMENT_CARD.MASTERCARD'] }];
            if (id == 'mobilepay')
                return [{ imageUrl: '/dist/assets/cards/MOBILEPAY.svg', alt: translations['PAYMENT_CARD.MOBILEPAY'] }];
            return [];
        }
        export const PaymentMethod = async (method: PaymentMethod, payment: PaymentReturnModel | null, translateService: TranslateService) => {
            return {
                value: method.id,
                label: method?.name ?? await lastValueFrom(translateService.get('PAYMENT.' + method.id.toUpperCase() + '_LABEL').pipe(filter(_ => _ != null), take(1))),
                name: 'paymentMethodId',
                type: 'radio',
                autocomplete: null,
                tag: 'input',
                attributes: { required: '', selected: payment ? '' : null },
                data: {
                    price: { amount: Math.max(0, (method?.amount?.amount ?? 0) - (method?.vat?.amount ?? 0)), currency: method?.amount?.currency },
                    vat: method?.vat,
                    description: method?.description ?? await lastValueFrom(translateService.get('PAYMENT.' + method.id.toUpperCase() + '_DESCRIPTION').pipe(filter(_ => _ != null), take(1))),
                    icons: PaymentMethodIcons(method.id)
                }
            } as InputModel
        };
        export const PaymentInvoiceOption = (option: InvoiceOption, payment: PaymentReturnModel) => ({
            value: 'option.id',
            label: 'option.label',
            name: 'invoiceOptionId',
            type: 'radio',
            tag: 'input',
            attributes: { required: '', selected: false ? '' : null }
        })
        export const PaymentMethodWithInvoiceOptions = async (method: PaymentMethod, payment: PaymentReturnModel | null, translateService: TranslateService, invoiceOptions?: Array<InvoiceOption>) => ({
            value: method.id,
            label: await lastValueFrom(translateService.get('PAYMENT.' + method.id.toUpperCase() + '_LABEL').pipe(filter(_ => _ != null), take(1))),
            name: 'paymentMethodId',
            type: 'radio',
            autocomplete: null,
            tag: 'input',
            attributes: { required: '', selected: payment ? '' : null, },
            data: { description: await lastValueFrom(translateService.get('PAYMENT.' + method.id.toUpperCase() + '_DESCRIPTION').pipe(filter(_ => _ != null), take(1))), icons: PaymentMethodIcons(method.id), options: (invoiceOptions ?? []).map(option => Input.PaymentInvoiceOption(option, payment)) }
        } as InputModel)

        export const Terms = async (translateService: TranslateService) => ({
            value: 'true',
            name: 'terms',
            type: 'checkbox',
            label: await lastValueFrom(translateService.get('CHECKOUT.TERMS_LABEL').pipe(filter(_ => _ != null), take(1))),
            attributes: {
                novalidate: '',
                required: '',
            }
        } as InputModel)
        export const News = async (translateService: TranslateService) => ({
            value: 'true',
            name: 'newsletter',
            type: 'checkbox',
            label: await lastValueFrom(translateService.get('CHECKOUT.NEWSLETTER_LABEL').pipe(filter(_ => _ != null), take(1))),
            attributes: {
                novalidate: '',
            }
        } as InputModel)
    }

    export namespace Forms {
        export const BillingPrivateAddress = (address?: Address, note?: string) => [
            {
                legend: 'ADDRESS.PRIVATE_ADDRESS_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.firstName('BILLING', address?.firstName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.lastName('BILLING', address?.lastName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.email('BILLING', address?.email), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.phoneNumber('BILLING', address?.phoneNumber), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.line1('BILLING', address?.line1), { attributes: { required: '', novalidate: '' } }),
                    Input.line2('BILLING', address?.line2),
                    DomUtils.mergeDeep(Input.postalCode('BILLING', address?.postalCode), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.city('BILLING', address?.city), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.country('BILLING', address?.country), { attributes: { required: '', novalidate: '' } }),
                ]
            },
            {
                legend: 'ADDRESS.NOTE_LEGEND',
                inputs: [Input.Note(note)]
            }
        ];
        export const BillingBusinessAddress = (address?: Address, note?: string, cvr?: string, addressBilling?: { email: string, reference: string }) => [
            {
                legend: 'ADDRESS.BUSINESS_ADDRESS_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.organizationName('BILLING', address?.organizationName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.CVR(cvr), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.line1('BILLING', address?.line1), { attributes: { required: '', novalidate: '' } }),
                    Input.line2('BILLING', address?.line2),
                    DomUtils.mergeDeep(Input.postalCode('BILLING', address?.postalCode), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.city('BILLING', address?.city), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.country('BILLING', address?.country), { attributes: { required: '', novalidate: '' } }),
                ]
            },
            {
                legend: 'ADDRESS.BUSINESS_CONTACT_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.firstName('BILLING', address?.firstName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.lastName('BILLING', address?.lastName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.email('BILLING', address?.email), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.phoneNumber('BILLING', address?.phoneNumber), { attributes: { required: '', novalidate: '' } }),
                ]
            },
            {
                legend: 'ADDRESS.BUSINESS_BILLING_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.email('BILLING', addressBilling?.email), { name: 'contact_email', placeholder: 'ADDRESS.BUSINESS_BILLING_EMAIL_PLACEHOLDER', autocomplete: 'off' }),
                    DomUtils.mergeDeep(Input.reference('BILLING', addressBilling?.reference), { name: 'contact_reference', autocomplete: 'off' }),
                ]
            },
            {
                legend: 'ADDRESS.NOTE_LEGEND',
                inputs: [Input.Note(note)]
            }
        ];
        export const BillingOrganizationAddress = (address?: Address, note?: string, gln?: string, addressBilling?: { email: string, reference: string }) => [
            {
                legend: 'ADDRESS.ORGANIZATION_ADDRESS_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.organizationName('BILLING', address?.organizationName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.GLN(gln), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.line1('BILLING', address?.line1), { attributes: { required: '', novalidate: '' } }),
                    Input.line2('BILLING', address?.line2),
                    DomUtils.mergeDeep(Input.postalCode('BILLING', address?.postalCode), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.city('BILLING', address?.city), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.country('BILLING', address?.country), { attributes: { required: '', novalidate: '' } }),
                ]
            },
            {
                legend: 'ADDRESS.ORGANIZATION_CONTACT_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.firstName('BILLING', address?.firstName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.lastName('BILLING', address?.lastName), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.email('BILLING', address?.email), { attributes: { required: '', novalidate: '' } }),
                    DomUtils.mergeDeep(Input.phoneNumber('BILLING', address?.phoneNumber), { attributes: { required: '', novalidate: '' } }),
                ]
            },
            {
                legend: 'ADDRESS.ORGANIZATION_BILLING_LEGEND',
                inputs: [
                    DomUtils.mergeDeep(Input.email('BILLING', addressBilling?.email), { name: 'contact_email', autocomplete: 'off' }),
                    DomUtils.mergeDeep(Input.reference('BILLING', addressBilling?.reference), { name: 'contact_reference', autocomplete: 'off' }),
                ]
            },
            {
                legend: 'ADDRESS.NOTE_LEGEND',
                inputs: [Input.Note(note)]
            }
        ];

        export const Shipping = async (basketService: BasketService) => {
            let methods: ShippingMethodReturnModel[] = basketService?.shippingMethods ?? [];
            let shipments: ShipmentReturnModel[] = basketService?.basket?.shipments ?? [];
            if (methods.length == 0)
                methods = (await basketService.get()).shippingMethods;
            let inputs = await Promise.all((methods ?? [])
                ?.filter(_ => _ != null)
                ?.map(async (method: ShippingMethodReturnModel) => {
                    if (method?.pickupPointProvider != null && method?.pickupPointProvider?.length > 0) {
                        let pickups: PickupPoint[] = await (method.pickupPointProvider?.length > 0 ? basketService.getPickupPoints(method.pickupPointProvider, basketService.basket.shippingAddress.postalCode) : []);
                        return Input.ShippingMethodWithPickups(method, shipments.find(s => s.shippingMethodId == method.id), pickups);
                    } else if (method != null)
                        return Input.ShippingMethod(method, shipments.find(s => s.shippingMethodId == method.id));
                    return;
                }))
            return [
                {
                    legend: 'SHIPPING.LEGEND',
                    inputs,
                }
            ]
        }
        export const Payment = async (basketService: BasketService, translateService: TranslateService): Promise<{ legend: string; inputs: InputModel[]; }[]> => {
            let methods = await basketService.getPaymentMethods();
            let payments = basketService.basket.payments;
            let inputs = await Promise.all((methods ?? [])
                ?.filter(_ => _ != null)
                ?.map((method: PaymentMethod) => Input
                    .PaymentMethod(method, payments.find(p => p.paymentMethodId == method.id), translateService)
                ));
            return [
                {
                    legend: 'PAYMENT.LEGEND',
                    inputs
                }
            ]
        }

    }

    export namespace SumbmitFunctions {
        function mapErrors(errors: SwaggerException) {
            return {
                title: '',
                text: errors.message,
                color: 'var(--error-color, white)',
                backgroundColor: 'var(--error-background-color, red)',
                position: 'top-mid'
            } as NotificationModel;
        }
        function mapNoteUpdateModel(form: FormData): string {
            return form.get('note') as string;
        }
        function mapAddressUpdateModel(form: FormData, basketService: BasketService): UpdateBasketAddressModel {
            var shippingAddress = basketService.basket.shippingAddress;
            var billingAddress = {
                firstName: form.get('firstName') as string,
                lastName: form.get('lastName') as string,
                organizationName: form.get('organizationName') as string,
                line1: form.get('line1') as string,
                line2: form.get('line2') as string,
                city: form.get('city') as string,
                postalCode: form.get('postalCode') as string,
                regionCode: form.get('regionCode') as string,
                country: form.get('country') as string,
                phoneNumber: form.get('phoneNumber') as string,
                mobilePhoneNumber: form.get('mobilePhoneNumber') as string,
                email: form.get('email') as string,
            };
            return { billingAddress, shippingAddress, };
        }

        function shouldSendInvoiceDetails(form: FormData): boolean {
            return [
                'contact_email',
                'contact_reference'
            ]
                .some(formPart =>
                    form.has(formPart) &&
                    (form.get(formPart) as string)?.trim()?.length > 0
                )
        }
        function mapInvoiceDetails(form: FormData): InvoiceDetailRequestModel {
            return {
                invoiceEmail: form.get('contact_email') as string,
                referenceNumber: form.get('contact_reference') as string
            }
        }
        function mapCvr(form: FormData): string {
            return form.get('cvr') as string;
        }
        function mapGln(form: FormData): string {
            return form.get('gln') as string;
        }
        export async function submitPayment(
            form: HTMLFormElement,
            basketService: BasketService,
            notificationService: ReplaySubject<NotificationModel>,
            stepper: StepperComponent,
        ) {
            var formData = new FormData(form);
            if (form.checkValidity() && formData.get('terms') as string == 'true') {
                var errors = [];
                await basketService
                    .setAcceptTerms(true)
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (formData.get('newsletter') as string == 'true') {
                    let billingAddress = basketService.basket.billingAddress;
                    await basketService
                        .setNewsLetter(true, Origin.Purchase, billingAddress.firstName, billingAddress.lastName, billingAddress.email, billingAddress.phoneNumber)
                        .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                }
                let paymentMethodId = formData.get('paymentMethodId') as string;
                let _url = await basketService
                    .purchase(paymentMethodId)
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (errors.length === 0 && typeof _url == 'string') {
                    let newBasket = basketService.basket;
                    GA4.track(MapToGA4.setPayment(newBasket, paymentMethodId))
                    window.location.href = _url
                }
                else
                    errors.forEach(e => notificationService.next(e));
            } else {
                // Fake input event for triggering validation
                var inputEvent = new Event('input', {
                    bubbles: true,
                });
                form
                    .querySelectorAll('input,textarea,select')
                    .forEach((elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => elm.dispatchEvent(inputEvent));
            }
        }
        export async function submitShipment(
            form: HTMLFormElement,
            basketService: BasketService,
            notificationService: ReplaySubject<NotificationModel>,
            stepper: StepperComponent,
        ) {
            if (form.checkValidity()) {
                var formData = new FormData(form);
                var errors = [];
                let updateShippingModel = Object.fromEntries(formData.entries());
                await basketService
                    .setShipment(updateShippingModel)
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (errors.length === 0) {
                    let newBasket = basketService.basket;
                    queueMicrotask(() =>
                        GA4.track(MapToGA4.setShipment(newBasket, updateShippingModel))
                    )
                    stepper?.next();
                }
                else
                    errors.forEach(e => notificationService.next(e));
            } else {
                // Fake input event for triggering validation
                var inputEvent = new Event('input', {
                    bubbles: true,
                });
                form
                    .querySelectorAll('input,textarea,select')
                    .forEach((elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => elm.dispatchEvent(inputEvent));
            }
        }
        export async function submitAddressPrivate(
            form: HTMLFormElement,
            basketService: BasketService,
            notificationService: ReplaySubject<NotificationModel>,
            stepper: StepperComponent,
        ) {

            if (form.checkValidity()) {
                var formData = new FormData(form);
                var errors = [];
                await basketService
                    .updateCustomer({ type: CustomerType.Private })
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateAddresses(mapAddressUpdateModel(formData, basketService))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateNote(mapNoteUpdateModel(formData))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (errors.length === 0) {

                    let newBasket = basketService.basket;
                    queueMicrotask(() =>
                        GA4.track(MapToGA4.checkoutStart(newBasket))
                    )
                    stepper?.next();
                }
                else
                    errors.forEach(e => notificationService.next(e));
            } else {
                // Fake input event for triggering validation
                var inputEvent = new Event('input', {
                    bubbles: true,
                });
                form
                    .querySelectorAll('input,textarea,select')
                    .forEach((elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => elm.dispatchEvent(inputEvent));
            }
        }

        export async function submitAddressBusiness(
            form: HTMLFormElement,
            basketService: BasketService,
            notificationService: ReplaySubject<NotificationModel>,
            stepper: StepperComponent,
        ) {
            if (form.checkValidity()) {
                var formData = new FormData(form);
                var errors = [];
                await basketService
                    .updateCustomer({ type: CustomerType.Company })
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateAddresses(mapAddressUpdateModel(formData, basketService))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateNote(mapNoteUpdateModel(formData))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (shouldSendInvoiceDetails(formData))
                    await basketService
                        .updateInvoiceDetails(mapInvoiceDetails(formData))
                        .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateCvr(mapCvr(formData))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (errors.length === 0) {

                    let newBasket = basketService.basket;
                    queueMicrotask(() =>
                        GA4.track(MapToGA4.checkoutStart(newBasket))
                    )
                    stepper?.next();
                }
                else
                    errors.forEach(e => notificationService.next(e));
            } else {
                // Fake input event for triggering validation
                var inputEvent = new Event('input', {
                    bubbles: true,
                });
                form
                    .querySelectorAll('input,textarea,select')
                    .forEach((elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => elm.dispatchEvent(inputEvent));
            }
        }
        export async function submitAddressOrganization(
            form: HTMLFormElement,
            basketService: BasketService,
            notificationService: ReplaySubject<NotificationModel>,
            stepper: StepperComponent,
        ) {
            if (form.checkValidity()) {
                var formData = new FormData(form);
                var errors = [];
                await basketService
                    .updateCustomer({ type: CustomerType.Public })
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateAddresses(mapAddressUpdateModel(formData, basketService))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateNote(mapNoteUpdateModel(formData))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (shouldSendInvoiceDetails(formData))
                    await basketService
                        .updateInvoiceDetails(mapInvoiceDetails(formData))
                        .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                await basketService
                    .updateGln(mapGln(formData))
                    .catch((e: SwaggerException) => errors.push(mapErrors(e)));
                if (errors.length === 0) {

                    let newBasket = basketService.basket;
                    queueMicrotask(() =>
                        GA4.track(MapToGA4.checkoutStart(newBasket))
                    )
                    stepper?.next();
                }
                else
                    errors.forEach(e => notificationService.next(e));
            } else {
                // Fake input event for triggering validation
                var inputEvent = new Event('input', {
                    bubbles: true,
                });
                form
                    .querySelectorAll('input,textarea,select')
                    .forEach((elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement) => elm.dispatchEvent(inputEvent));
            }
        }
    }

    export namespace Mapper {
        export function cvr(basket: BasketReturnModel): string {
            return basket?.cvr ?? '';
        }
        export function gln(basket: BasketReturnModel): string {
            return basket?.gln ?? '';
        }
        export function contactAddress(basket: BasketReturnModel): { email: string, reference: string } {
            return { email: basket?.invoiceDetail?.emails?.find(() => true) ?? '', reference: basket?.invoiceDetail?.referenceNumber };
        }
    }

    export namespace UpdateForm {
        export function setAddress(form: HTMLFormElement, address: Address) {
            Object
                .entries(address)
                .filter(([key, value]) => value != null)
                .forEach(([key, value]) => {
                    var elm: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement = (form.querySelector(`input[name="${key}"],textarea[name="${key}"]`) as HTMLInputElement | HTMLTextAreaElement);
                    if (elm != null)
                        elm.value = value;
                    else {
                        elm = (form.querySelector(`select[name="${key}"]`) as HTMLSelectElement);
                        elm
                            .querySelectorAll('option')
                            .forEach(o => o.selected = o.value === value);
                    }
                }
                )
        }
        export function cvrOrGln(form: HTMLFormElement, vat: string) {
            var elm = form.querySelector(`input[name="cvr"],input[name="gln"]`) as HTMLInputElement;
            elm.value = vat;
        }
    }
}
